import { Button, useAlert } from '@ads/components-react';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useIndividualPaymentHeader } from '@/features/individual-payment/hooks/useIndividualPaymentHeader';
import { useIndividualPaymentParcels } from '@/features/individual-payment/hooks/useIndividualPaymentParcels';
import type {
  ConfirmableAction,
  ParcelActionRequest,
} from '@/features/individual-payment/hooks/useParcelActionRunner';
import { useParcelActionRunner } from '@/features/individual-payment/hooks/useParcelActionRunner';
import type { FuturePaymentProvider } from '@/features/individual-payment/hooks/useParcelFuturePayment';
import { FINANCIAL_PERSON_STATUS_INACTIVE } from '@/features/individual-payment/types/individualPayment';
import type {
  ConfirmableContractAction,
  ConfirmableParcelOperation,
  ContractHeaderAction,
  ParcelOperation,
} from '@/features/individual-payment/types/parcels';
import {
  EContractHeaderAction,
  EParcelOperation,
  isBatchContractAction,
  isConfirmableContractAction,
  isConfirmableParcelOperation,
} from '@/features/individual-payment/types/parcels';
import type { PaymentHistoryMode } from '@/features/individual-payment/types/paymentHistory';
import { ContractDiscountsPanel } from '@/features/individual-payment/ui/molecules/ContractDiscountsPanel';
import { ContractValidationsAlert } from '@/features/individual-payment/ui/molecules/ContractValidationsAlert';
import { ContractHeaderCard } from '@/features/individual-payment/ui/organisms/ContractHeaderCard';
import { ParcelActionConfirmModal } from '@/features/individual-payment/ui/organisms/ParcelActionConfirmModal';
import { ParcelBilletModal } from '@/features/individual-payment/ui/organisms/ParcelBilletModal';
import { ParcelFuturePaymentModal } from '@/features/individual-payment/ui/organisms/ParcelFuturePaymentModal';
import { ParcelHistoryDrawer } from '@/features/individual-payment/ui/organisms/ParcelHistoryDrawer';
import { ParcelRefundModal } from '@/features/individual-payment/ui/organisms/ParcelRefundModal';
import { ParcelsTable } from '@/features/individual-payment/ui/organisms/ParcelsTable';
import { IndividualPaymentParcelsTemplate } from '@/features/individual-payment/ui/templates/IndividualPaymentParcelsTemplate';
import { deriveBatchTargets } from '@/features/individual-payment/utils/deriveBatchTargets';
import { deriveParcelSummary } from '@/features/individual-payment/utils/deriveParcelSummary';
import { EMPTY_VALUE } from '@/features/individual-payment/utils/formatCurrency';
import { EPaymentType } from '@/features/individual-payment/utils/parcelOperationAvailability';
import { ERole } from '@/shared/lib/roles';
import { usePermissions } from '@/shared/store/hooks/usePermissions';

export const Route = createFileRoute(
  '/_authenticated/individual-payment/$financialPersonId/parcels/',
)({
  component: IndividualPaymentParcelsPage,
});

/**
 * Rótulo de cada ação da régua do cabeçalho — usado só na mensagem de "ainda não
 * disponível". Chaves literais para que `t()` valide em tempo de compilação; um
 * `t('parcels.headerActions.' + action)` montado em runtime perderia a checagem.
 */
const HEADER_ACTION_LABEL_KEY = {
  editPerson: 'parcels.headerActions.editPerson',
  editPayment: 'parcels.headerActions.editPayment',
  downloadBooklet: 'parcels.headerActions.downloadBooklet',
  addParcel: 'parcels.headerActions.addParcel',
  cancelOpenParcels: 'parcels.headerActions.cancelOpenParcels',
  openCancelledParcels: 'parcels.headerActions.openCancelledParcels',
  resetExtendedExpiration: 'parcels.headerActions.resetExtendedExpiration',
  renegotiation: 'parcels.headerActions.renegotiation',
  inactivateFinancial: 'parcels.headerActions.inactivateFinancial',
} as const satisfies Record<ContractHeaderAction, string>;

/** Mesma ideia para as operações do menu da parcela. */
const PARCEL_OPERATION_LABEL_KEY = {
  pay: 'parcels.operations.pay',
  edit: 'parcels.operations.edit',
  generateBillet: 'parcels.operations.generateBillet',
  sendCharge: 'parcels.operations.sendCharge',
  sendEmail: 'parcels.operations.sendEmail',
  reversePayment: 'parcels.operations.reversePayment',
  refundPayment: 'parcels.operations.refundPayment',
  activate: 'parcels.operations.activate',
  cancel: 'parcels.operations.cancel',
  reintegrate: 'parcels.operations.reintegrate',
  resetIntegration: 'parcels.operations.resetIntegration',
  takeAdvantageParcel: 'parcels.operations.takeAdvantageParcel',
  openInvoice: 'parcels.operations.openInvoice',
  invoiceFuturePayment: 'parcels.operations.invoiceFuturePayment',
  invoiceFuturePaymentVanzolini: 'parcels.operations.invoiceFuturePaymentVanzolini',
  cancelInvoice: 'parcels.operations.cancelInvoice',
  traceability: 'parcels.operations.traceability',
  orderTracking: 'parcels.operations.orderTracking',
  downloadPdf: 'parcels.operations.downloadPdf',
} as const satisfies Record<ParcelOperation, string>;

/** Mensagem de sucesso de cada ação confirmada. */
const SUCCESS_KEY = {
  cancel: 'parcels.confirm.cancel.success',
  activate: 'parcels.confirm.activate.success',
  reversePayment: 'parcels.confirm.reversePayment.success',
  resetIntegration: 'parcels.confirm.resetIntegration.success',
  reintegrate: 'parcels.confirm.reintegrate.success',
  sendCharge: 'parcels.confirm.sendCharge.success',
  takeAdvantageParcel: 'parcels.confirm.takeAdvantageParcel.success',
  cancelInvoice: 'parcels.confirm.cancelInvoice.success',
  cancelOpenParcels: 'parcels.confirm.cancelOpenParcels.success',
  openCancelledParcels: 'parcels.confirm.openCancelledParcels.success',
  inactivateFinancial: 'parcels.confirm.inactivateFinancial.success',
} as const satisfies Record<ConfirmableAction, string>;

/**
 * Ação aguardando confirmação. O escopo é explícito porque quem abre já sabe de
 * onde veio — o menu da parcela ou a régua do contrato — e é ele que decide o
 * payload da request.
 */
type ConfirmState =
  | {
      readonly scope: 'parcel';
      readonly action: ConfirmableParcelOperation;
      readonly parcelId: number;
      /** Boleto alvo, quando a ação partiu da linha de um. */
      readonly serial?: number | undefined;
    }
  | { readonly scope: 'contract'; readonly action: ConfirmableContractAction };

/**
 * Monta a request da ação confirmada.
 *
 * Função pura e fora do componente porque cada ação carrega um payload
 * diferente: encadear isso em ternários dentro do `handleConfirm` esconderia
 * qual dado vai em qual ação.
 *
 * @param confirm - Ação confirmada e seu alvo.
 * @param reversePayment - Valor da opção booleana do cancelamento de NF.
 * @param batchIds - Ids derivados para as duas ações em lote.
 * @returns Payload pronto para o `run` do {@link useParcelActionRunner}.
 */
function buildActionRequest(
  confirm: ConfirmState,
  reversePayment: boolean,
  batchIds: readonly number[],
): ParcelActionRequest {
  if (confirm.scope === 'contract') {
    return isBatchContractAction(confirm.action)
      ? { action: confirm.action, parcelIds: batchIds }
      : { action: confirm.action };
  }

  if (confirm.action === EParcelOperation.Reintegrate) {
    return {
      action: 'reintegrate',
      parcelId: confirm.parcelId,
      ...(confirm.serial === undefined ? {} : { serial: confirm.serial }),
    };
  }

  if (confirm.action === EParcelOperation.CancelInvoice) {
    return { action: 'cancelInvoice', parcelId: confirm.parcelId, reversePayment };
  }

  return { action: confirm.action, parcelId: confirm.parcelId };
}

/**
 * Página de visualização de parcelas de um contrato financeiro.
 *
 * Orquestra template, cabeçalho do contrato e tabela de parcelas. Header e
 * parcelas são requisições independentes: uma falha nas parcelas não impede a
 * leitura do cabeçalho, e o erro fica local à tabela.
 */
export function IndividualPaymentParcelsPage() {
  const { t } = useTranslation('individualPayment');
  const { alert } = useAlert();
  const navigate = useNavigate();
  const { financialPersonId: rawId } = Route.useParams();
  const { hasRole } = usePermissions();
  const isSecretary = hasRole(ERole.Secretary);

  // O parâmetro de rota é string. Converter aqui, na fronteira, evita propagar
  // `NaN` para a URL de duas requisições.
  const financialPersonId = useMemo(() => {
    const parsed = Number(rawId);
    return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
  }, [rawId]);

  const {
    header,
    isLoading: isHeaderLoading,
    isError: isHeaderError,
    refetch: refetchHeader,
  } = useIndividualPaymentHeader(financialPersonId);

  const {
    parcels,
    isLoading: areParcelsLoading,
    isError: areParcelsError,
    refetch: refetchParcels,
  } = useIndividualPaymentParcels(financialPersonId);

  const summary = useMemo(() => deriveParcelSummary(parcels), [parcels]);

  // Painel de histórico da parcela. Vive na página, não na tabela: é uma camada
  // sobre toda a tela, e só uma parcela tem histórico aberto por vez.
  const [history, setHistory] = useState<{
    readonly parcelId: number;
    readonly mode: PaymentHistoryMode;
  } | null>(null);

  // Ação aguardando confirmação. Um único modal atende os dois escopos: só uma
  // ação pode estar em confirmação por vez.
  const [confirm, setConfirm] = useState<ConfirmState | null>(null);

  // Parcela com o diálogo de antecipação de NF aberto, e por qual parceiro.
  // Fica fora do `confirm` porque o diálogo tem formulário e mutation própria, e
  // não passa pelo `useParcelActionRunner` — que só despacha ações sem payload.
  const [futurePayment, setFuturePayment] = useState<{
    readonly parcelId: number;
    readonly provider: FuturePaymentProvider;
  } | null>(null);

  // Parcela com o diálogo de devolução aberto. Fora do `confirm` porque o
  // diálogo tem valor, motivo e mutation próprios.
  const [refundParcelId, setRefundParcelId] = useState<number | null>(null);

  // Parcela com o diálogo de boleto aberto. Também fora do `confirm`: são dois
  // caminhos, e um deles espera o provedor.
  const [billetParcelId, setBilletParcelId] = useState<number | null>(null);

  // Opção do cancelamento de NF: estornar também o pagamento. Vive na página
  // porque é ela que monta a request; começa desmarcada a cada abertura, para o
  // estorno nunca ser herdado de uma confirmação anterior.
  const [reverseInvoicePayment, setReverseInvoicePayment] = useState(false);

  const { run, reset, isPending, error } = useParcelActionRunner(financialPersonId);

  // Os dois conjuntos das ações em lote saem da mesma lista já carregada: servem
  // para desabilitar o item quando não há parcela elegível e para montar o
  // payload da request quando há.
  const openTargets = useMemo(() => deriveBatchTargets(parcels, 'cancelOpenParcels'), [parcels]);
  const cancelledTargets = useMemo(
    () => deriveBatchTargets(parcels, 'openCancelledParcels'),
    [parcels],
  );

  /**
   * O que a régua superior esconde, pelas regras do grid legado.
   *
   * - **Inativar financeiro** sai quando o contrato já está inativo
   *   (`StatusId != ECourseFinancialPersonStatus.Inactive`). No cabeçalho o campo
   *   numérico se chama `status` — o `statusText` ao lado é só para exibir.
   * - **Editar pagamento** e **Adicionar parcela** não são de secretária: são as
   *   duas ações de escrita da régua, e o legado as condiciona a `!isSecretary`.
   * - **Download do carnê** só aparece em boleto comum (`PaymentType == 1`).
   *   Boleto PecegePay (6) **não** exibe, e é por isso que a condição testa o
   *   valor exato em vez de "é algum tipo de boleto".
   *
   * Enquanto o header não carrega, `paymentType` é `undefined` e o carnê fica
   * escondido — lado seguro: o botão entra quando o dado chega, em vez de aparecer
   * e sumir.
   */
  const hiddenActions = useMemo<readonly ContractHeaderAction[]>(
    () => [
      ...(header?.status === FINANCIAL_PERSON_STATUS_INACTIVE
        ? ([EContractHeaderAction.InactivateFinancial] as const)
        : []),
      ...(isSecretary
        ? ([EContractHeaderAction.EditPayment, EContractHeaderAction.AddParcel] as const)
        : []),
      ...(header?.paymentType === EPaymentType.Billet
        ? []
        : ([EContractHeaderAction.DownloadBooklet] as const)),
    ],
    [header?.status, header?.paymentType, isSecretary],
  );

  const disabledActions = useMemo<readonly ContractHeaderAction[]>(
    () => [
      ...(openTargets.count === 0 ? (['cancelOpenParcels'] as const) : []),
      ...(cancelledTargets.count === 0 ? (['openCancelledParcels'] as const) : []),
    ],
    [openTargets.count, cancelledTargets.count],
  );

  const batchTargets =
    confirm?.scope === 'contract' && isBatchContractAction(confirm.action)
      ? confirm.action === EContractHeaderAction.CancelOpenParcels
        ? openTargets
        : cancelledTargets
      : null;

  const notImplemented = (label: string) =>
    alert({ alertType: 'warning', title: t('parcels.notImplemented', { action: label }) });

  /** Abre o modal já limpando o estado da ação anterior. */
  const openConfirm = (next: ConfirmState) => {
    reset();
    setReverseInvoicePayment(false);
    setConfirm(next);
  };

  const closeConfirm = () => {
    setConfirm(null);
    reset();
  };

  const handleConfirm = () => {
    if (confirm === null) return;

    const request = buildActionRequest(confirm, reverseInvoicePayment, batchTargets?.ids ?? []);

    const count = batchTargets?.count ?? 0;

    run(request, {
      onSuccess: () => {
        setConfirm(null);
        reset();
        alert({ alertType: 'success', title: t(SUCCESS_KEY[confirm.action], { count }) });
      },
    });
  };

  const handleHeaderAction = (action: ContractHeaderAction) => {
    if (action === EContractHeaderAction.EditPerson) {
      // `/persons/$id` é a tela de edição do cadastro (`PersonEditPage` +
      // `PersonForm`), não uma tela de leitura — é o destino certo do botão.
      if (header?.personId == null) {
        // O botão já vem desabilitado nesse caso; o alerta é a rede de segurança
        // para não engolir a ação em silêncio se ela virar acionável.
        alert({
          alertType: 'warning',
          title: t('parcels.headerActions.editPersonUnavailable'),
        });
        return;
      }
      void navigate({ to: '/persons/$id', params: { id: String(header.personId) } });
      return;
    }

    if (action === EContractHeaderAction.EditPayment) {
      void navigate({
        to: '/individual-payment/$financialPersonId',
        params: { financialPersonId: rawId },
        search: { tab: 'detalhes' },
      });
      return;
    }

    if (action === EContractHeaderAction.AddParcel) {
      void navigate({
        to: '/individual-payment/$financialPersonId/parcels/create',
        params: { financialPersonId: rawId },
      });
      return;
    }

    if (action === EContractHeaderAction.DownloadBooklet) {
      // Não existe endpoint de carnê consolidado em /api/v1/individualpayment/*
      // — só `parcels/{parcelId}/invoice-file`, por parcela. A mensagem é
      // específica para o operador não achar que é falha de permissão.
      alert({ alertType: 'warning', title: t('parcels.bookletUnavailable') });
      return;
    }

    // Cancelar abertas, reabrir canceladas e inativar financeiro: só disparam um
    // endpoint, então vão para o modal de confirmação.
    if (isConfirmableContractAction(action)) {
      openConfirm({ scope: 'contract', action });
      return;
    }

    notImplemented(t(HEADER_ACTION_LABEL_KEY[action]));
  };

  const handleParcelOperation = (operation: ParcelOperation, parcelId: number, serial?: number) => {
    const parcel = parcels.find(p => p.id === parcelId);

    // As duas operações de leitura abrem o painel de histórico.
    if (
      operation === EParcelOperation.Traceability ||
      operation === EParcelOperation.OrderTracking
    ) {
      setHistory({ parcelId, mode: operation });
      return;
    }

    // Acessar a NF é saída da aplicação: o link da prefeitura vem na própria
    // linha. `noopener,noreferrer` porque a aba nova é de terceiro.
    if (operation === EParcelOperation.OpenInvoice) {
      const link = parcel?.linkNfeCityHall ?? '';
      if (link === '') {
        alert({ alertType: 'warning', title: t('parcels.openInvoiceUnavailable') });
        return;
      }
      window.open(link, '_blank', 'noopener,noreferrer');
      return;
    }

    // Editar tem tela própria: o formulário completo da parcela.
    if (operation === EParcelOperation.Edit) {
      void navigate({
        to: '/individual-payment/$financialPersonId/parcels/$parcelId/edit',
        params: { financialPersonId: rawId, parcelId: String(parcelId) },
      });
      return;
    }

    // Antecipar a NF: um endpoint por parceiro, mas com corpo da NF e e-mail
    // informados no diálogo — modal próprio, fora do de confirmação. O parceiro
    // muda só a mutation e o título.
    if (
      operation === EParcelOperation.InvoiceFuturePayment ||
      operation === EParcelOperation.InvoiceFuturePaymentVanzolini
    ) {
      setFuturePayment({
        parcelId,
        provider:
          operation === EParcelOperation.InvoiceFuturePaymentVanzolini ? 'vanzolini' : 'fealq',
      });
      return;
    }

    // Devolver o pagamento: um endpoint, mas com valor e motivo informados no
    // diálogo — modal próprio, fora do de confirmação.
    if (operation === EParcelOperation.RefundPayment) {
      setRefundParcelId(parcelId);
      return;
    }

    // Gerar boleto tem dois caminhos (baixar direto ou registrar no PecegePay com
    // acompanhamento), decididos pelos flags da parcela — daí o diálogo próprio.
    if (operation === EParcelOperation.GenerateBillet) {
      setBilletParcelId(parcelId);
      return;
    }

    // As nove que só disparam um endpoint vão para o modal de confirmação. O
    // `serial` só chega quando a ação partiu da linha de um boleto, e hoje só
    // `reintegrate` o consome.
    if (isConfirmableParcelOperation(operation)) {
      openConfirm({ scope: 'parcel', action: operation, parcelId, serial });
      return;
    }

    // Sobra `pay`, que depende de um formulário próprio, e `downloadPdf`, que a
    // linha do boleto resolve sozinha pela URL da resposta.
    notImplemented(t(PARCEL_OPERATION_LABEL_KEY[operation]));
  };

  if (financialPersonId === null) {
    return (
      <div className="flex flex-col items-center gap-micro rounded-sm border-hairline border-ctx-layout-border bg-ctx-layout-surface p-xl">
        <span className="flex items-center justify-center rounded-pill bg-base-danger-100 p-micro">
          <ExclamationTriangleIcon className="h-xxs w-xxs text-base-danger-700" />
        </span>
        <p className="ts-heading-xs text-ctx-content-title">{t('parcels.invalidId')}</p>
        <p className="ts-paragraph-xs text-ctx-content-placeholder">{t('parcels.invalidIdHint')}</p>
        <Button
          hierarchy="secondary"
          size="md"
          onClick={() => void navigate({ to: '/individual-payment' })}
        >
          {t('parcels.backToList')}
        </Button>
      </div>
    );
  }

  if (isHeaderError) {
    return (
      <div className="flex flex-col items-center gap-micro rounded-sm border-hairline border-ctx-layout-border bg-ctx-layout-surface p-xl">
        <span className="flex items-center justify-center rounded-pill bg-base-danger-100 p-micro">
          <ExclamationTriangleIcon className="h-xxs w-xxs text-base-danger-700" />
        </span>
        <p className="ts-heading-xs text-ctx-content-title">{t('parcels.headerError')}</p>
        <p className="ts-paragraph-xs text-ctx-content-placeholder">
          {t('parcels.table.errorHint')}
        </p>
        <Button hierarchy="secondary" size="md" onClick={() => void refetchHeader()}>
          {t('parcels.table.retry')}
        </Button>
      </div>
    );
  }

  return (
    <IndividualPaymentParcelsTemplate
      studentName={header?.studentName ?? EMPTY_VALUE}
      courseName={header?.courseName}
      canEditPerson={header?.personId != null}
      disabledActions={disabledActions}
      hiddenActions={hiddenActions}
      onAction={handleHeaderAction}
    >
      <ContractValidationsAlert validations={header?.validations} />

      <ContractHeaderCard
        header={header}
        isLoading={isHeaderLoading}
        summary={areParcelsLoading ? null : summary}
      />

      <ContractDiscountsPanel discounts={header?.discounts} />

      <ParcelsTable
        parcels={parcels}
        isLoading={areParcelsLoading}
        isError={areParcelsError}
        summary={summary}
        contractPaymentType={header?.paymentType}
        onRetry={() => void refetchParcels()}
        onOperation={handleParcelOperation}
      />

      <ParcelHistoryDrawer
        parcelId={history?.parcelId ?? null}
        mode={history?.mode ?? null}
        parcelNumber={
          history ? parcels.find(p => p.id === history.parcelId)?.numberParcel : undefined
        }
        onClose={() => setHistory(null)}
      />

      <ParcelFuturePaymentModal
        financialPersonId={financialPersonId}
        parcelId={futurePayment?.parcelId ?? null}
        parcel={
          futurePayment === null ? undefined : parcels.find(p => p.id === futurePayment.parcelId)
        }
        numberParcels={header?.numberParcels}
        provider={futurePayment?.provider}
        onClose={() => setFuturePayment(null)}
        onMarked={() => {
          setFuturePayment(null);
          alert({ alertType: 'success', title: t('parcels.futurePayment.success') });
        }}
      />

      <ParcelRefundModal
        financialPersonId={financialPersonId}
        parcelId={refundParcelId}
        parcel={refundParcelId === null ? undefined : parcels.find(p => p.id === refundParcelId)}
        numberParcels={header?.numberParcels}
        onClose={() => setRefundParcelId(null)}
        onRefunded={() => {
          setRefundParcelId(null);
          alert({ alertType: 'success', title: t('parcels.refund.success') });
        }}
      />

      <ParcelBilletModal
        financialPersonId={financialPersonId}
        parcelId={billetParcelId}
        parcel={billetParcelId === null ? undefined : parcels.find(p => p.id === billetParcelId)}
        numberParcels={header?.numberParcels}
        onClose={() => setBilletParcelId(null)}
      />

      <ParcelActionConfirmModal
        action={confirm?.action ?? null}
        parcel={
          confirm?.scope === 'parcel' ? parcels.find(p => p.id === confirm.parcelId) : undefined
        }
        batch={batchTargets}
        header={header}
        openParcelsCount={openTargets.count}
        numberParcels={header?.numberParcels}
        serial={confirm?.scope === 'parcel' ? confirm.serial : undefined}
        toggleValue={reverseInvoicePayment}
        onToggleChange={setReverseInvoicePayment}
        isPending={isPending}
        error={error}
        onConfirm={handleConfirm}
        onClose={closeConfirm}
      />
    </IndividualPaymentParcelsTemplate>
  );
}
